import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_OPTIONS = [
  { id: 'editor', label: 'Editor' },
  { id: 'code', label: 'Code' },
  { id: 'preview', label: 'Preview' },
];

export const PremiumWysiwygEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('editor');
  const [codeValue, setCodeValue] = useState(value || '');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const nextValue = value || '';
    setCodeValue(nextValue);
    if (editorRef.current && editorRef.current.innerHTML !== nextValue) {
      editorRef.current.innerHTML = nextValue;
    }
  }, [value]);

  const syncEditorValue = () => {
    const html = editorRef.current?.innerHTML || '';
    setCodeValue(html);
    onChange(html);
  };

  const applyCommand = (command, commandValue = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    syncEditorValue();
  };

  const insertHtml = (html) => {
    applyCommand('insertHTML', html);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'product-descriptions');

      const response = await axios.post(`${API}/storage/upload`, formData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.url) {
        applyCommand('insertImage', response.data.url);
      }
    } catch {
      // keep editor usable even if upload fails
    }
  };

  const handlePrint = () => {
    const html = codeValue || '';
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><head><title>Print</title></head><body>${html}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleFindReplace = () => {
    const findTerm = window.prompt('Find text:');
    if (!findTerm) return;
    const replaceWith = window.prompt('Replace with:', '') ?? '';
    const escaped = findTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'g');
    const next = (codeValue || '').replace(regex, replaceWith);
    setCodeValue(next);
    onChange(next);
    if (editorRef.current) editorRef.current.innerHTML = next;
  };

  const handleCodeChange = (nextValue) => {
    setCodeValue(nextValue);
    onChange(nextValue);
    if (editorRef.current) editorRef.current.innerHTML = nextValue;
  };

  return (
    <div
      className={`${isFullscreen ? 'fixed inset-0 z-[100] bg-white p-4' : 'border border-gray-300 rounded-lg overflow-hidden bg-white'}`}
      data-testid="premium-wysiwyg-editor-container"
    >
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 p-2 flex-wrap" data-testid="premium-wysiwyg-editor-tabs-row">
        {TAB_OPTIONS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant={activeTab === tab.id ? 'default' : 'outline'}
            className={activeTab === tab.id ? 'bg-[#6e2ea8] hover:bg-[#5b2490]' : ''}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`premium-wysiwyg-tab-${tab.id}`}
          >
            {tab.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button type="button" variant="outline" onClick={handleFindReplace} data-testid="premium-wysiwyg-find-replace-button">Find/Replace</Button>
        <Button type="button" variant="outline" onClick={handlePrint} data-testid="premium-wysiwyg-print-button">Print</Button>
        <Button type="button" variant="outline" onClick={() => setIsFullscreen((prev) => !prev)} data-testid="premium-wysiwyg-fullscreen-button">
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </Button>
      </div>

      {activeTab === 'editor' && (
        <div data-testid="premium-wysiwyg-editor-pane">
          <div className="border-b border-gray-200 bg-white p-2 flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={() => applyCommand('undo')}>Undo</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('redo')}>Redo</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('bold')}><strong>B</strong></Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('italic')}><em>I</em></Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('underline')}><u>U</u></Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('strikeThrough')}>S</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('insertUnorderedList')}>• List</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('insertOrderedList')}>1. List</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('justifyLeft')}>Left</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('justifyCenter')}>Center</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('justifyRight')}>Right</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('justifyFull')}>Justify</Button>
            <select className="border rounded px-2 py-1 text-sm" onChange={(e) => applyCommand('fontName', e.target.value)} data-testid="premium-wysiwyg-font-select" defaultValue="">
              <option value="" disabled>Font</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Georgia">Georgia</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Verdana">Verdana</option>
            </select>
            <select className="border rounded px-2 py-1 text-sm" onChange={(e) => applyCommand('fontSize', e.target.value)} data-testid="premium-wysiwyg-size-select" defaultValue="">
              <option value="" disabled>Size</option>
              <option value="1">10</option>
              <option value="2">13</option>
              <option value="3">16</option>
              <option value="4">18</option>
              <option value="5">24</option>
              <option value="6">32</option>
              <option value="7">48</option>
            </select>
            <input type="color" className="w-9 h-9 border rounded" onChange={(e) => applyCommand('foreColor', e.target.value)} title="Text color" data-testid="premium-wysiwyg-text-color-input" />
            <input type="color" className="w-9 h-9 border rounded" onChange={(e) => applyCommand('hiliteColor', e.target.value)} title="Highlight color" data-testid="premium-wysiwyg-highlight-color-input" />
            <Button type="button" variant="outline" onClick={() => {
              const url = window.prompt('Enter link URL');
              if (url) applyCommand('createLink', url);
            }}>Link</Button>
            <Button type="button" variant="outline" onClick={() => {
              const url = window.prompt('Enter image URL');
              if (url) applyCommand('insertImage', url);
            }}>Image URL</Button>
            <Button type="button" variant="outline" onClick={() => imageInputRef.current?.click()} data-testid="premium-wysiwyg-image-upload-button">Upload Image</Button>
            <Button type="button" variant="outline" onClick={() => {
              const url = window.prompt('Enter video embed URL');
              if (url) insertHtml(`<div><iframe src="${url}" style="width:100%;min-height:280px" frameborder="0" allowfullscreen></iframe></div>`);
            }}>Video</Button>
            <Button type="button" variant="outline" onClick={() => insertHtml('<table border="1" style="border-collapse:collapse;width:100%"><tbody><tr><td>Cell 1</td><td>Cell 2</td></tr><tr><td>Cell 3</td><td>Cell 4</td></tr></tbody></table>')}>Table</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('formatBlock', 'h1')}>H1</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('formatBlock', 'h2')}>H2</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('formatBlock', 'blockquote')}>Quote</Button>
            <Button type="button" variant="outline" onClick={() => insertHtml('<hr/>')}>HR</Button>
            <Button type="button" variant="outline" onClick={() => applyCommand('removeFormat')}>Clear</Button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => handleImageUpload(event.target.files?.[0])}
              data-testid="premium-wysiwyg-image-upload-input"
            />
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="w-full min-h-[340px] p-4 outline-none prose max-w-none"
            onInput={syncEditorValue}
            data-testid="premium-wysiwyg-contenteditable"
          />
        </div>
      )}

      {activeTab === 'code' && (
        <div className="p-3" data-testid="premium-wysiwyg-code-pane">
          <textarea
            value={codeValue}
            onChange={(event) => handleCodeChange(event.target.value)}
            className="w-full min-h-[340px] rounded-md border border-gray-300 bg-[#0b1021] text-[#d6e2ff] p-3 font-mono text-sm"
            data-testid="premium-wysiwyg-code-input"
          />
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="p-4 min-h-[340px] bg-white" data-testid="premium-wysiwyg-preview-pane">
          {codeValue?.trim() ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: codeValue }} />
          ) : (
            <p className="text-gray-500" data-testid="premium-wysiwyg-preview-empty">No preview content yet.</p>
          )}
        </div>
      )}
    </div>
  );
};
